/**
 * TvCard — Triveny surface card with hairline border + shadow-1.
 *
 *   <TvCard
 *     header={<TvLabel>Today's tasks</TvLabel>}
 *     headerActions={<TvPill>{count}</TvPill>}
 *   >
 *     {rows}
 *   </TvCard>
 *
 * Pass `accent` for the "Start here" NBA emphasis: cyan border + shadow-2.
 *
 * The card body is a plain children slot — composing list rows, KPI grids,
 * or freeform content is up to the caller. The header is rendered ONLY if
 * `header` (or `headerActions`) is provided.
 */
import React from 'react';
import { tvTokens } from './tokens';

interface TvCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Card title node — typically <TvLabel>…</TvLabel>. Renders nothing if omitted. */
  header?: React.ReactNode;
  /** Right-aligned slot in the header row — pills, buttons, etc. */
  headerActions?: React.ReactNode;
  /** When true, swaps border → accent + shadow → shadow-2 (NBA emphasis). */
  accent?: boolean;
  /** Override body padding. Default is none — caller controls internal layout. */
  bodyPadding?: number | string;
  children: React.ReactNode;
}

export function TvCard({
  header,
  headerActions,
  accent = false,
  bodyPadding,
  children,
  style,
  ...rest
}: TvCardProps) {
  const hasHeader = header !== undefined || headerActions !== undefined;
  return (
    <div
      {...rest}
      style={{
        background: tvTokens.surface,
        border: `1px solid ${accent ? tvTokens.accent : tvTokens.border}`,
        borderRadius: tvTokens.rLg,
        boxShadow: accent ? tvTokens.shadow2 : tvTokens.shadow1,
        overflow: 'hidden',
        ...style,
      }}
    >
      {hasHeader && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '13px 16px',
            borderBottom: `1px solid ${tvTokens.borderMuted}`,
          }}
        >
          {header}
          {headerActions !== undefined && (
            <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {headerActions}
            </span>
          )}
        </div>
      )}
      <div style={bodyPadding !== undefined ? { padding: bodyPadding } : undefined}>{children}</div>
    </div>
  );
}
